import { type RefObject, useRef } from "react";
import { mayLeavePage } from "../navigation/leaveGuard";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  FiBriefcase,
  FiChevronUp,
  FiLogOut,
  FiRepeat,
  FiSettings,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { EmailInviteLanguage } from "../../autogen/bff-api";
import { reachesAccessManagement } from "../organization/roleAdministration";
import { urls } from "../../urls";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useLogout } from "../auth/useLogout";
import {
  getPreferredLanguage,
  setPreferredLanguage,
} from "../local-storage/language";
import { Explanation } from "../support/Explanation/Explanation";
import { Tooltip } from "../components/Tooltip";
import { useChangeOrganizationModal } from "./useChangeOrganizationModal";
import { useViewAsRoleLabel, ViewAsRoleOptions } from "./ViewAsRoleOptions";

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const LANGUAGE_NAMES: Record<EmailInviteLanguage, string> = {
  [EmailInviteLanguage.No]: "Norsk",
  [EmailInviteLanguage.En]: "English",
};

export const SidebarUserMenu = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation();
  const logout = useLogout();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const leaveOrPointBack = (leave: () => void) => {
    if (mayLeavePage()) {
      leave();
      return;
    }
    pointBackWhenMenuTakesFocus(menuRef);
  };
  const go = (to: string) => leaveOrPointBack(() => navigate(to));
  const { me, selectedOrg, organizations } = useLoggedInWithOrgContextState();
  const changeOrganizationModal = useChangeOrganizationModal();
  const hasMultipleOrganizations = organizations.length > 1;
  const language =
    getPreferredLanguage() === EmailInviteLanguage.No
      ? EmailInviteLanguage.No
      : EmailInviteLanguage.En;
  const viewingAs = useViewAsRoleLabel();

  const switchLanguage = (next: EmailInviteLanguage) => {
    if (next === language) return;
    setPreferredLanguage(next);
    window.location.reload();
  };

  const fullName = `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim();
  const compactLabel = viewingAs
    ? `${fullName} · ${t("Viewing as")} ${viewingAs}`
    : fullName;
  const initials = (
    <Flex
      w="36px"
      h="36px"
      rounded="full"
      bg="smPrimarySofter"
      color="smPrimary"
      fontSize="13px"
      fontWeight="bold"
      align="center"
      justify="center"
      flexShrink={0}
      borderWidth={viewingAs ? "2px" : "0"}
      borderColor="smPrimary"
    >
      {getInitials(fullName)}
    </Flex>
  );

  return (
    <Box ref={menuRef} flexShrink={0} px={compact ? "2" : "3"} py="3">
      {changeOrganizationModal.modal}
      <Menu placement="top-start" matchWidth={!compact} isLazy>
        {compact ? (
          <Tooltip label={compactLabel} placement="right">
            <MenuButton
              as={Button}
              variant="ghost"
              w="full"
              h="auto"
              px="0"
              py="2"
              aria-label={compactLabel}
              _hover={{ bg: "smBackgroundSecondary" }}
              _active={{ bg: "smBackgroundSecondary" }}
            >
              <Flex justify="center">{initials}</Flex>
            </MenuButton>
          </Tooltip>
        ) : (
          <MenuButton
            as={Button}
            variant="ghost"
            w="full"
            h="auto"
            px="2"
            py="2"
            _hover={{ bg: "smBackgroundSecondary" }}
            _active={{ bg: "smBackgroundSecondary" }}
          >
            <HStack spacing="2.5">
              {initials}
              <Box flex="1" minW="0" textAlign="left">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="smText"
                  noOfLines={1}
                >
                  {fullName}
                </Text>
                <Text
                  fontSize="11px"
                  color={viewingAs ? "smPrimary" : "smMuted"}
                  noOfLines={1}
                >
                  {viewingAs
                    ? `${t("Viewing as")} ${viewingAs}`
                    : selectedOrg.name}
                </Text>
              </Box>
              <Icon as={FiChevronUp} color="smMuted" flexShrink={0} />
            </HStack>
          </MenuButton>
        )}
        <MenuList maxH="80vh" overflowY="auto">
          <ViewAsRoleOptions />
          <MenuOptionGroup
            title={t("Language") ?? undefined}
            type="radio"
            value={language}
            onChange={(value) => switchLanguage(value as EmailInviteLanguage)}
          >
            {[EmailInviteLanguage.No, EmailInviteLanguage.En].map((option) => (
              <MenuItemOption key={option} value={option}>
                {LANGUAGE_NAMES[option]}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
          <MenuDivider />
          <MenuItem
            icon={<Icon as={FiUser} />}
            onClick={() => go(urls.app.profile)}
          >
            {t("Profile")}
          </MenuItem>
          <MenuItem
            icon={<Icon as={FiBriefcase} />}
            onClick={() => go(urls.app.organizationProfile.go(selectedOrg.id))}
          >
            {selectedOrg.name}
          </MenuItem>
          <MenuItem
            icon={<Icon as={FiSettings} />}
            onClick={() => go(urls.app.settings)}
          >
            {t("Settings")}
          </MenuItem>
          <Explanation
            text={t("Only admins can manage users")}
            enabled={!reachesAccessManagement(selectedOrg)}
            placement="right-end"
          >
            <MenuItem
              icon={<Icon as={FiUsers} />}
              isDisabled={!reachesAccessManagement(selectedOrg)}
              onClick={() => go(urls.app.users)}
            >
              {t("Access management")}
            </MenuItem>
          </Explanation>
          {hasMultipleOrganizations && (
            <MenuItem
              icon={<Icon as={FiRepeat} />}
              onClick={() => changeOrganizationModal.show()}
            >
              {t("Switch organization")}
            </MenuItem>
          )}
          {me.canOpenAdminDashboard && (
            <MenuItem
              icon={<Icon as={FiShield} />}
              onClick={() => go(urls.admin)}
            >
              {t("Admin dashboard")}
            </MenuItem>
          )}
          <MenuDivider />
          <MenuItem
            icon={<Icon as={FiLogOut} />}
            color="smError"
            onClick={() => {
              leaveOrPointBack(() => void logout());
            }}
          >
            {t("Log out")}
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

const MENU_SETTLES_WITHIN_MS = 1000;

const pointBackWhenMenuTakesFocus = (menuRef: RefObject<HTMLElement>) => {
  const pointBack = (event: FocusEvent) => {
    if (!(event.target instanceof Node) || !menuRef.current?.contains(event.target)) return;
    stop();
    mayLeavePage();
  };
  const stop = () => {
    document.removeEventListener("focusin", pointBack);
    window.clearTimeout(timer);
  };
  document.addEventListener("focusin", pointBack);
  const timer = window.setTimeout(stop, MENU_SETTLES_WITHIN_MS);
};
